<template>
  <div
    id="main-online-samples-record"
    :class="[
      `layout-${layoutMode}`,
      { 'online-samples-record--empty-state': !hasOnlineSamples },
    ]"
  >
    <template v-if="hasOnlineSamples">
      <div class="dashboard-bottom-col-title">
        <div class="dashboard-bottom-col-header-name">
          <img
            :src="onlineSamplesRecordIconSvg"
            style="
              width: 25px;
              height: 25px;
              display: block;
              object-fit: contain;
            "
            alt="icon"
          />
          <h2 class="onClick" @click="navigateToSampleList">
            {{ $t('onlineSamplesRecord.title') }}
          </h2>
        </div>
        <div
          v-if="canRegisterSamples"
          class="dashboard-bottom-col-header-button"
        >
          <button
            class="button-primary"
            data-tour-id="samples-register-dashboard-button"
            @click="goSampleRegister"
          >
            {{ $t('onlineSamplesRecord.register') }}
          </button>
          <button
            class="button-secondary"
            data-tour-id="samples-register-templates-dashboard-button"
            @click="manageTemplates"
          >
            <img
              :src="iconDocumentTemplate"
              alt=""
              aria-hidden="true"
              class="button-secondary-icon"
            />
            {{ $t('manageTemplates') }}
          </button>
        </div>
      </div>
      <div class="dashboard-bottom-col-body">
        <div class="dashboard-bottom-col-content">
          <div
            v-for="card in statusCards"
            :key="card.key"
            class="dashboard-bottom-col-content-item record-status-card is-clickable"
            :class="[
              `record-status-card--${card.variant}`,
              `record-status-card--layout-${layoutMode}`,
            ]"
            @click="navigateToSampleList(card.statusCode)"
          >
            <div class="record-status-card-top">
              <div
                class="dashboard-bottom-col-content-item-title record-status-card-title"
              >
                <div class="record-status-card-icon">
                  <img
                    class="status-icon"
                    :src="card.icon"
                    :alt="card.title"
                  />
                </div>
                <div
                  class="dashboard-bottom-col-content-item-text record-status-card-text"
                >
                  <p class="record-status-card-label">
                    {{ card.title }}
                  </p>
                  <p class="record-status-card-metric">
                    <span class="record-status-card-value">
                      {{ card.value }}
                    </span>
                    <span class="record-status-card-detail">
                      {{ $t('onlineSamplesRecord.shipments') }}
                    </span>
                  </p>
                </div>
              </div>

              <span
                class="sample-card-arrow f7-icons"
                aria-hidden="true"
              >
                chevron_right
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="samples-record-empty-state" role="status">
      <div class="samples-record-empty-state__left">
        <div class="samples-record-empty-state__head">
          <img
            :src="onlineSamplesRecordIconSvg"
            alt=""
            aria-hidden="true"
          />
          <h2>{{ $t('samplesRecordEmptyState.title') }}</h2>
        </div>
        <img
          class="samples-record-empty-state__illustration"
          :src="samplesRecordEmptyStateIconSvg"
          alt=""
          aria-hidden="true"
        />
      </div>

      <div class="samples-record-empty-state__right">
        <h3>{{ $t('samplesRecordEmptyState.heading') }}</h3>
        <p>{{ $t('samplesRecordEmptyState.description') }}</p>

        <div class="samples-record-empty-state__badges">
          <span
            v-for="feature in emptyStateFeatures"
            :key="feature.key"
            class="samples-record-empty-state__badge"
          >
            <span
              class="samples-record-empty-state__badge-icon"
              :style="{ '--badge-icon-url': `url(${feature.icon})` }"
              aria-hidden="true"
            ></span>
            <span>{{ feature.label }}</span>
          </span>
        </div>

        <div class="samples-record-empty-state__actions">
          <button
            v-if="canRegisterSamples"
            class="button-primary samples-record-empty-state__cta"
            data-tour-id="samples-register-dashboard-button"
            @click="goSampleRegister"
          >
            {{ $t('samplesRecordEmptyState.primaryAction') }}
          </button>
          <button
            class="button-secondary samples-record-empty-state__cta samples-record-empty-state__cta--secondary"
            @click="manageTemplates"
          >
            <img
              :src="iconDocumentTemplate"
              alt=""
              aria-hidden="true"
              class="button-secondary-icon"
            />
            {{ $t('manageTemplates') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { NOTIFY_TIME_LONG } from '@/js/constants';
import { mapActions } from 'vuex';
import onlineSamplesRecordIconSvg from '@/static/icons/registro_online.svg';
import samplesRecordEmptyStateIconSvg from '@/static/icons/registro-Muestras-Empty-State-Icon.svg';
import deliveredIconSvg from '@/static/icons/recibida.svg';
import onTheWayIconSvg from '@/static/icons/en_camino.svg';
import openIconSvg from '@/static/icons/abierta.svg';
import fastIconSvg from '@/static/icons/destello.svg';
import customizableIconSvg from '@/static/icons/ic_pencil.svg';
import multipleSamplesIconSvg from '@/static/icons/ic_stack.svg';
import iconDocumentTemplate from '@/static/icons/ic_document_template.svg';

export default {
  props: {
    onlineSamplesRecord: { type: Object, default: () => ({}) },
    layoutMode: {
      type: String,
      default: 'grid',
    },
    canRegisterSamples: {
      type: Boolean,
      default: true,
    },
    hasOnlineSamples: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    onlineSamplesRecordIconSvg: () => onlineSamplesRecordIconSvg,
    samplesRecordEmptyStateIconSvg: () =>
      samplesRecordEmptyStateIconSvg,
    deliveredIconSvg: () => deliveredIconSvg,
    onTheWayIconSvg: () => onTheWayIconSvg,
    openIconSvg: () => openIconSvg,
    iconDocumentTemplate: () => iconDocumentTemplate,
    company() {
      return this.$store.state.contact.company;
    },
    statusCards() {
      return [
        {
          key: 'received',
          title: this.$t('onlineSamplesRecord.reception'),
          value: this.onlineSamplesRecord.NumRecepcionadas,
          icon: this.deliveredIconSvg,
          variant: 'success',
          statusCode: '102',
        },
        {
          key: 'on-the-way',
          title: this.$t('onlineSamplesRecord.onTheWay'),
          value: this.onlineSamplesRecord.NumEnTransito,
          icon: this.onTheWayIconSvg,
          variant: 'warning',
          statusCode: '101',
        },
        {
          key: 'open',
          title: this.$t('onlineSamplesRecord.openRequest'),
          value: this.onlineSamplesRecord.NumSolicitudesAbiertas,
          icon: this.openIconSvg,
          variant: 'primary',
          statusCode: '100',
        },
      ];
    },
    emptyStateFeatures() {
      return [
        {
          key: 'fast',
          label: this.$t('samplesRecordEmptyState.features.fast'),
          icon: fastIconSvg,
        },
        // Reused icon: ic_pencil.svg (customization/edit meaning)
        {
          key: 'customizable',
          label: this.$t(
            'samplesRecordEmptyState.features.customizable'
          ),
          icon: customizableIconSvg,
        },
        // Reused icon: ic_stack.svg (multiple grouped samples)
        {
          key: 'multipleSamples',
          label: this.$t(
            'samplesRecordEmptyState.features.multipleSamples'
          ),
          icon: multipleSamplesIconSvg,
        },
        // Reused icon: ic_document_template.svg (template/document)
        {
          key: 'templates',
          label: this.$t(
            'samplesRecordEmptyState.features.templates'
          ),
          icon: iconDocumentTemplate,
        },
      ];
    },
  },
  methods: {
    navigateToSampleList(statusCode = null) {
      const query = statusCode
        ? { invoiceStatuses: statusCode }
        : undefined;
      this.$f7router.navigate(
        { name: 'SamplesList', query },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    goSampleRegister() {
      if (!this.canRegisterSamples) {
        return;
      }

      try {
        this.setInvoiceSamples([]);
        this.RegisterInvoice();
        this.$f7router.navigate(
          {
            name: 'SamplesRegister',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
    },
    manageTemplates() {
      this.$f7router.navigate(
        {
          name: 'TemplatesListPage',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    ...mapActions('samplesRegisterModule', [
      'setInvoiceSamples',
      'RegisterInvoice',
    ]),
  },
};
</script>
<style scoped lang="scss">
@import '@/components/Dashboard/OnlineSamplesRecord/style.scss';
</style>
